import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import type { SetStateAction } from '../hooks/createStore';
import {
  LauncherElementPropInput,
  LauncherElementType,
} from '../info/launcherElementInfo';
import { UIData, useUIStore } from '../hooks/stores/uiStore';
import { SessionData, useSessionStore } from '../hooks/stores/sessionStore';
import { LauncherWindowData, useLauncherWindowStore } from '../hooks/stores/launcherWindowStore';
import { LauncherElementData, TreeNode, useLauncherElementsStore } from '../hooks/stores/launcherElementsStore';
import { LauncherStatesData, useLauncherStatesStore } from '../hooks/stores/launcherStatesStore';
import { stateViewsInfo } from '../info/stateViewsInfo';
import { createHistoryUtils, HistoryState } from './scene/historyUtils';
import { createSceneManipulationUtils } from './scene/sceneManipulationUtils';
import { createModalUtils } from './scene/modalUtils';
import { createElementEditingUtils } from './scene/elementEditingUtils';
import { createElementSelectionUtils } from './scene/elementSelectionUtils';
import { createCommandUtils } from './scene/commandUtils';
import { createElementManipulationUtils } from './scene/elementManipulationUtils';
import { createCopyPasteUtils } from './scene/copyPasteUtils';
import { createVisibilityUtils } from './scene/visibilityUtils';
import { createConfigUtils } from './scene/configUtils';
import { createElementDataUtils } from './scene/elementDataUtils';
import { createLauncherStateUtils } from './scene/launcherStateUtils';

export type { HistoryState };

export const SceneCtx = createContext<SceneData | null>(null);
export const useScene = () => useContext(SceneCtx)!;

export function SceneContextProvider({ children }: { children: ReactNode }) {
  const [UIData, setUIData] = useUIStore<UIData>();

  // Create a wrapper function that matches the type expected by createCommandUtils
  const setUIDataWrapper = useCallback(
    (data: Partial<UIData> | ((prev: UIData) => Partial<UIData>)) => {
      setUIData(data as SetStateAction<UIData, keyof UIData>);
    },
    [setUIData]
  );

  const [sessionData, setSessionData] = useSessionStore<SessionData>();
  const [launcherWindowData, setLauncherWindowData] =
    useLauncherWindowStore<LauncherWindowData>();
  const [launcherElementsData, setLauncherElementsData] =
    useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherStatesData, setLauncherStatesData] =
    useLauncherStatesStore<LauncherStatesData>();
  const isHistoryActionInProgress = useRef(false);
  const [copiedElements, setCopiedElements] = useState<LauncherElementData[]>();

  const [importCanBeDone, setImportCanBeDone] = useState(false);
  const [configJSON, setConfigJSON] = useState('');

  const {
    importConfig,
    exportConfig,
    saveThemeVersion,
    executeImportConfig,
  } = createConfigUtils({
    UIData,
    setUIData,
    sessionData,
    setSessionData,
    launcherWindowData,
    setLauncherWindowData,
    launcherElementsData,
    setLauncherElementsData,
    importCanBeDone,
    setImportCanBeDone,
    configJSON,
    setConfigJSON,
  });

  // Execute import
  useEffect(() => {
    if (importCanBeDone) {
      executeImportConfig(configJSON);
      setImportCanBeDone(false);
    }
  }, [importCanBeDone, configJSON]);

  const {
    updateElementData,
    updateSelectedElementsData,
    getElementData,
    getSelectedElementsData,
    getSelectedElementsPropInputs,
    getElementComputedStyle,
  } = createElementDataUtils({
    UIData,
    setUIData,
    launcherElementsData,
    setLauncherElementsData,
  });

  const {
    historySave,
    historyUndo,
    historyRedo,
  } = createHistoryUtils({
    UIData,
    setUIData,
    isHistoryActionInProgress,
    exportConfig,
    importConfig,
  });

  const {
    resetPosition,
    zoomIn,
    zoomOut,
    resetZoom,
  } = createSceneManipulationUtils({
    UIData,
    setUIData,
  });

  const {
    openModal,
    closeModal,
    toggleModal,
  } = createModalUtils({
    UIData,
    setUIData,
  });

  const {
    selectElement,
    selectOneElement,
    deselectElement,
    deselectAllElements,
    hoverElement,
    hoverOneElement,
    unhoverElement,
    unhoverAllElements,
  } = createElementSelectionUtils({
    UIData,
    setUIData,
  });

  const {
    startEditingElement,
    endEditingElement,
  } = createElementEditingUtils({
    UIData,
    setUIData,
    updateElementData,
  });

  const {
    setLauncherStates,
    setLauncherView,
    resetLauncherStates,
    getCurrentView,
    getStatesForView,
    isViewActive,
  } = createLauncherStateUtils({
    UIData,
    setUIData,
    launcherStatesData,
    setLauncherStatesData,
  });

  const {
    executeCommand,
  } = createCommandUtils({
    UIData,
    setUIData: setUIDataWrapper,
    setLauncherStatesData,
    setLauncherView,
  });

  const {
    deleteElement,
    deleteSelectedElements,
    moveElementsToAnotherParent,
    renameElement,
    addElement,
  } = createElementManipulationUtils({
    UIData,
    setUIData,
    launcherWindowData,
    setLauncherWindowData,
    launcherElementsData,
    setLauncherElementsData,
    updateElementData,
    deselectAllElements,
    selectOneElement,
  });

  const {
    getElementDirectVisibility,
    getSelectedElementsDirectVisibility,
    getElementComputedVisibility,
    getSelectedElementsComputedVisibility,
    setElementVisibility,
    setSelectedElementsVisibility,
  } = createVisibilityUtils({
    UIData,
    setUIData,
    launcherElementsData,
    setLauncherElementsData,
    launcherStatesData,
    launcherWindowData,
  });

  const {
    duplicateSelectedElements,
    copySelectedElements,
    pasteElements,
  } = createCopyPasteUtils({
    UIData,
    setUIData,
    launcherWindowData,
    setLauncherWindowData,
    launcherElementsData,
    setLauncherElementsData,
    copiedElements,
    setCopiedElements,
  });

  const value = useMemo(
    () => ({
      // Config management
      importConfig,
      exportConfig,
      saveThemeVersion,

      // History
      historySave,
      historyUndo,
      historyRedo,

      // Scene manipulation
      resetPosition,
      zoomIn,
      zoomOut,
      resetZoom,
      executeCommand,

      // Element selection
      selectElement,
      selectOneElement,
      deselectElement,
      deselectAllElements,
      hoverElement,
      hoverOneElement,
      unhoverElement,
      unhoverAllElements,

      // Element manipulation
      duplicateSelectedElements,
      copySelectedElements,
      pasteElements,
      deleteElement,
      deleteSelectedElements,
      moveElementsToAnotherParent,
      renameElement,
      addElement,

      // Element data
      updateElementData,
      updateSelectedElementsData,
      getElementData,
      getSelectedElementsData,
      getSelectedElementsPropInputs,
      getElementComputedStyle,

      // Visibility
      getElementDirectVisibility,
      getSelectedElementsDirectVisibility,
      getElementComputedVisibility,
      getSelectedElementsComputedVisibility,
      setElementVisibility,
      setSelectedElementsVisibility,

      // Element editing
      startEditingElement,
      endEditingElement,

      // Modal management
      openModal,
      closeModal,
      toggleModal,

      // Launcher state management
      setLauncherStates,
      setLauncherView,
      resetLauncherStates,
      getCurrentView,
      getStatesForView,
      isViewActive,

      // UIData management
      setUIData,
    }),
    [
      // Config management
      importConfig,
      exportConfig,
      saveThemeVersion,

      // History
      historySave,
      historyUndo,
      historyRedo,

      // Scene manipulation
      resetPosition,
      zoomIn,
      zoomOut,
      resetZoom,
      executeCommand,

      // Element selection
      selectElement,
      selectOneElement,
      deselectElement,
      deselectAllElements,
      hoverElement,
      hoverOneElement,
      unhoverElement,
      unhoverAllElements,

      // Element manipulation
      duplicateSelectedElements,
      copySelectedElements,
      pasteElements,
      deleteElement,
      deleteSelectedElements,
      moveElementsToAnotherParent,
      renameElement,
      addElement,

      // Element data
      updateElementData,
      updateSelectedElementsData,
      getElementData,
      getSelectedElementsData,
      getSelectedElementsPropInputs,
      getElementComputedStyle,

      // Visibility
      getElementDirectVisibility,
      getSelectedElementsDirectVisibility,
      getElementComputedVisibility,
      getSelectedElementsComputedVisibility,
      setElementVisibility,
      setSelectedElementsVisibility,

      // Element editing
      startEditingElement,
      endEditingElement,

      // Modal management
      openModal,
      closeModal,
      toggleModal,

      // Launcher state management
      setLauncherStates,
      setLauncherView,
      resetLauncherStates,
      getCurrentView,
      getStatesForView,
      isViewActive,

      // UIData management
      setUIData,
    ]
  );

  return (
    <SceneCtx.Provider
      value={value}
    >
      {children}
    </SceneCtx.Provider>
  );
}

interface SceneData {
  importConfig: (configJSON: string, quietConversion?: boolean) => void;
  exportConfig: () => string;
  saveThemeVersion: () => void;

  resetPosition: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  executeCommand: (command: {
    type: string;
    appProcessExtraArgsAsText?: string;
    linkUrl?: string;
    popupId?: string;
  }) => void;
  historySave: () => void;
  historyUndo: () => void;
  historyRedo: () => void;

  selectElement: (id: string) => void;
  selectOneElement: (id: string) => void;
  deselectElement: (id: string) => void;
  deselectAllElements: () => void;
  hoverElement: (id: string) => void;
  hoverOneElement: (id: string) => void;
  unhoverElement: (id: string) => void;
  unhoverAllElements: () => void;

  duplicateSelectedElements: () => void;
  copySelectedElements: () => void;
  pasteElements: () => void;
  deleteElement: (id: string) => void;
  deleteSelectedElements: () => void;
  moveElementsToAnotherParent: ({
    elementIDs,
    parentID,
    index,
  }: {
    elementIDs: string[];
    parentID: string;
    index: number;
  }) => void;
  renameElement: (id: string, name: string) => void;
  updateElementData: (
    id: string,
    data: { [key: string]: any },
    propertyPath?: string
  ) => void;
  updateSelectedElementsData: (
    data: { [key: string]: any },
    propertyPath?: string,
    inputType?: LauncherElementPropInput
  ) => void;
  getElementData: (
    id: string,
    defaultObj: Record<string, any>,
    propertyPath?: string
  ) => any;
  getSelectedElementsData: (
    defaultObj: Record<string, any>,
    propertyPath?: string
  ) => Record<string, any>;
  getSelectedElementsPropInputs: () => LauncherElementPropInput[];

  getElementDirectVisibility: (
    id: string,
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ) => boolean | undefined;
  getSelectedElementsDirectVisibility: (
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ) => boolean | undefined;
  getElementComputedVisibility: (
    id: string,
    launcherState?: [keyof LauncherStatesData | null, string | null],
    launcherStates?: LauncherStatesData
  ) => boolean | undefined;
  getSelectedElementsComputedVisibility: (
    launcherState?: [keyof LauncherStatesData | null, string | null],
    launcherStates?: LauncherStatesData
  ) => boolean | undefined;
  setElementVisibility: (
    id: string,
    value?: boolean,
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ) => void;
  setSelectedElementsVisibility: (
    value?: boolean,
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ) => void;

  getElementComputedStyle: (id: string) => CSSStyleDeclaration | null;
  addElement: (
    elementType: LauncherElementType,
    data?: Partial<LauncherElementData>
  ) => void;
  startEditingElement: (id: string) => void;
  endEditingElement: (newTextValue?: string | null) => void;

  openModal: (modalID: string) => void;
  closeModal: (modalID: string) => void;
  toggleModal: (modalID: string) => void;

  setLauncherStates: (states: LauncherStatesData) => void;
  setLauncherView: (view: keyof typeof stateViewsInfo) => void;
  resetLauncherStates: () => void;
  getCurrentView: () => string | null;
  getStatesForView: (view: keyof typeof stateViewsInfo) => Partial<LauncherStatesData>;
  isViewActive: (view: keyof typeof stateViewsInfo) => boolean;
  setUIData: (data: Partial<UIData>) => void;
}
